import { Link } from "react-router-dom";
import type { ArticleItem, PostItem, VideoItem } from "@/types/content";

interface LatestSectionProps {
  videos: VideoItem[];
  articles: ArticleItem[];
  posts: PostItem[];
}

export default function LatestSection({ videos, articles, posts }: LatestSectionProps) {
  const hasVideos = videos.length > 0;
  const hasArticles = articles.length > 0;
  const hasPosts = posts.length > 0;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base text-foreground-950 md:text-lg">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-800">
              <i className="ri-play-circle-line text-base"></i>
            </span>
            最新视频
          </h3>
          <Link to="/videos" className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-900">
            全部
          </Link>
        </div>
        {hasVideos ? (
          <ul className="flex flex-col divide-y divide-background-100">
            {videos.map((video) => (
              <li key={video.id}>
                <Link to={`/videos/detail/${video.id}`} className="group flex gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-16 md:w-24">
                    <img
                      src={video.cover}
                      alt={`${video.title}的封面缩略图`}
                      title={`${video.title} - 挠痒视频`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                      {video.title}
                    </p>
                    <p className="mt-1.5 flex items-center gap-2 text-[11px] text-foreground-500">
                      <span className="whitespace-nowrap">{video.date}</span>
                      <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                      <span className="truncate">{video.author}</span>
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-10 text-center text-sm text-foreground-500">暂无最新内容，敬请期待</p>
        )}
      </section>

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base text-foreground-950 md:text-lg">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800">
              <i className="ri-article-line text-base"></i>
            </span>
            最新文章
          </h3>
          <Link to="/articles" className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-900">
            全部
          </Link>
        </div>
        {hasArticles ? (
          <ul className="flex flex-col divide-y divide-background-100">
            {articles.map((article) => (
              <li key={article.id}>
                <Link to={`/articles/detail/${article.id}`} className="group flex gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-16 md:w-24">
                    <img
                      src={article.cover}
                      alt={`${article.title}的配图缩略图`}
                      title={`${article.title} - 趣味文章`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900 transition-colors group-hover:text-accent-700">
                      {article.title}
                    </p>
                    <p className="mt-1.5 flex items-center gap-2 text-[11px] text-foreground-500">
                      <span className="whitespace-nowrap">{article.date}</span>
                      <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                      <span className="truncate">{article.tag}</span>
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-10 text-center text-sm text-foreground-500">暂无最新内容，敬请期待</p>
        )}
      </section>

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base text-foreground-950 md:text-lg">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
              <i className="ri-chat-smile-2-line text-base"></i>
            </span>
            最新帖子
          </h3>
          <Link to="/community" className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-900">
            全部
          </Link>
        </div>
        {hasPosts ? (
          <ul className="flex flex-col divide-y divide-background-100">
            {posts.map((post) => (
              <li key={post.id}>
                <Link to={`/community/detail/${post.id}`} className="group block py-3 first:pt-0 last:pb-0">
                  <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900 transition-colors group-hover:text-secondary-700">
                    {post.title}
                  </p>
                  <p className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                    <span className="truncate">{post.author}</span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-chat-3-line"></i>
                      {post.replyCount}
                    </span>
                    <span className="whitespace-nowrap text-foreground-400">{post.updatedAt.slice(5, 10)}</span>
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-10 text-center text-sm text-foreground-500">暂无最新内容，敬请期待</p>
        )}
      </section>
    </div>
  );
}